import {
  Button,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { useEffect, useState, type ReactNode, useMemo, useRef } from 'react';
import { ArrowDownward, Delete, Edit, Visibility } from '@mui/icons-material';
import FileSaver from 'file-saver';
import moment from 'moment-timezone';
import { useRecoilState } from 'recoil';

import DataTable, { type TableColumn } from 'components/common/DataTable';
import MyModal from 'components/common/modal';
import CustomContainer from 'components/CustomContainer';
import AddEventTableModal from 'components/modals/AddEventModal';
import EditEventTableModal from 'components/modals/EditEventTableModal';
import type { FetchedEvent } from 'components/modals/types';
import { useFetchBuoys, useFetchEventTypes } from './state';

import api from 'utils/api';
import ViewEventModal from 'components/modals/ViewEventModal';

interface GetEvents {
  data: Array<FetchedEvent>;
  meta: { total: number; limit: number; page: number };
}

function EventsLog() {
  const [fetchedData, setFetchedData] = useState<Array<FetchedEvent>>([]);
  const [fetchedMetaData, setFetchedMetaData] = useState<{
    total?: number;
    limit?: number;
    page?: number;
  }>({});
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState<FetchedEvent | null>(null);
  const [viewRow, setViewRow] = useState<FetchedEvent | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const [currentLimit, setCurrentLimit] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // State to indicate CSV generation in progress
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);
  const didMountRef = useRef<boolean>(false);
  const suppressPageChangeRef = useRef<boolean>(false);
  const { buoys } = useFetchBuoys();
  const { eventTypes } = useFetchEventTypes();

  const EVENTS_API = '/admin/events';

  const [sortState, setSortState] = useState<{
    id: string;
    direction: 'asc' | 'desc';
  }>({ id: 'startTime', direction: 'desc' });

  const [filterLocation, setFilterLocation] = useState<string>('ALL-ALL');
  const [filterEvent, setFilterEvent] = useState<string>('ALL-ALL');
  const [dataTableKey, setDataTableKey] = useState<number>(1);

  // =============================================================================
  // Table Specification
  const columns: Array<TableColumn<FetchedEvent>> = [
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      width: '100px',
      id: 'id'
    },
    {
      name: 'Start Time',
      selector: (row) => formatDate(row.startTs),
      sortable: true,
      width: '200px',
      id: 'startTime'
    },
    {
      name: 'Location',
      selector: (row) => row.locationFF,
      sortable: true,
      width: '150px',
      id: 'location'
    },
    {
      name: 'Event',
      selector: (row) => row.eventType,
      sortable: true,
      width: '200px',
      id: 'event'
    },
    {
      name: 'Logged By',
      selector: (row) => row.createdByName,
      sortable: false,
      width: '150px',
      id: 'loggedBy'
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: '8px'
          }}
        >
          <IconButton
            type='button'
            onClick={() => {
              handleDelete(row);
            }}
            sx={{
              borderRadius: 2,
              border: 1,
              height: 36,
              width: 36,
              borderColor: `rgb(13, 54, 80)`,
              '& svg': {
                fill: `rgb(13, 54, 80)`
              },
              '&:hover': {
                borderColor: `rgb(61, 162, 144)`,
                '& svg': {
                  fill: `rgb(61, 162, 144)`
                }
              }
            }}
          >
            <Delete />
            <span className='sr-only'>Delete</span>
          </IconButton>
          <IconButton
            type='button'
            onClick={() => {
              handleEdit(row);
            }}
            sx={{
              borderRadius: 2,
              height: 36,
              width: 36,
              backgroundColor: `rgb(239, 190, 55)`,
              '&:hover': {
                backgroundColor: `rgb(60, 161, 159)`
              },
              '& svg': {
                fill: `rgb(255,255,255)`
              }
            }}
          >
            <Edit />
            <span className='sr-only'>Edit</span>
          </IconButton>
          <IconButton
            type='button'
            onClick={() => {
              handleView(row);
            }}
            sx={{
              borderRadius: 2,
              height: 36,
              width: 36,
              backgroundColor: `rgb(37, 111, 148)`,
              '&:hover': {
                backgroundColor: `rgb(60, 161, 159)`
              },
              '& svg': {
                fill: `rgb(255,255,255)`
              }
            }}
          >
            <Visibility />
            <span className='sr-only'>View</span>
          </IconButton>
        </div>
      )
    }
  ];

  // =============================================================================
  // useEffect functions for table actions
  useEffect(() => {
    setSortState({
      id: 'startTime',
      direction: 'desc'
    });
    setDataTableKey((prev) => prev + 1);
    fetchTableData();
    //cleanTableData();
  }, [refreshTable, currentPage, currentLimit, filterLocation, filterEvent]);

  const handleView = (row: FetchedEvent) => {
    setViewRow(row);
    setIsViewModalOpen(true);
  };

  const handleEdit = (row: FetchedEvent) => {
    setEditRow(row);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (row: FetchedEvent) => {
    if (confirm(`Do you want to delete the Event: ${row.eventType}`)) {
      try {
        await api.delete(EVENTS_API + '/' + row.id, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        handleRefreshTable();
      } catch (err) {
        window.alert('Server error, please try again later!');
      }
    }
  };

  const handleAddEvent = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
  };

  const handleRefreshTable = () => {
    setRefreshTable(!refreshTable);
  };

  const fetchTableData = async (): Promise<void> => {
    try {
      setLoading(true);
      const currentData = await api
        .get(EVENTS_API, {
          headers: {
            'Content-Type': 'application/json'
          },
          params: {
            limit: currentLimit,
            page: currentPage,
            buoyId: filterLocation.split('-')[0],
            eventTypeId: filterEvent.split('-')[0]
          }
        })
        .then((res) => res.data as GetEvents);
      setFetchedData(currentData.data);
      setFetchedMetaData(currentData.meta);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: unknown) => {
    const date = new Date(Number(dateString));
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  };

  // =============================================================================
  // CSV Download Helper Functions
  const formatTimestampForCsv = (ts: unknown) =>
    moment
      .utc(Number(ts))
      .tz('Asia/Singapore')
      .format('YYYY-MM-DDTHH:mm:ss.SSSZ');

  const csvEscape = (value: unknown) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (/[",\r\n]/.test(str)) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const handleDownloadCsv = async () => {
    try {
      setIsCsvDownloading(true);

      const eventsWithSensors: Array<FetchedEvent> = await api
        .get('/admin/events/full', {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        .then((res) => res.data);

      const headers = [
        'Id',
        'Location',
        'Event Type',
        'Comment',
        'Created By',
        'Start Time',
        'End Time',
        'Created At',
        'Sensors'
      ];

      const rows = [
        headers.map(csvEscape),
        ...eventsWithSensors.map((ev) =>
          [
            ev.id,
            ev.locationFF,
            ev.eventType,
            ev.comment,
            ev.createdByName,
            formatTimestampForCsv(ev.startTs),
            formatTimestampForCsv(ev.endTs),
            formatTimestampForCsv(ev.createdAt ?? ev.createdOn),
            ev.sensors ?? ''
          ].map(csvEscape)
        )
      ];

      const csvFile = new File(
        [rows.map((row) => row.join(',')).join('\r\n')],
        `events_${new Date().toISOString()}.csv`,
        {
          type: 'text/csv;charset=utf-8'
        }
      );

      FileSaver.saveAs(csvFile);
    } catch (err) {
      console.error(err);
      window.alert('Failed to download CSV.');
    } finally {
      setIsCsvDownloading(false);
    }
  };

  // =============================================================================
  // Styling
  const customStyles = {
    headRow: {
      style: {
        color: 'white',
        backgroundColor: '#6699CC' //blue
      }
    },
    headCells: {
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        border: '1px solid #ddd', // add border to header cells
        justifyContent: 'center'
      }
    },
    rows: {
      style: {
        minHeight: '56px',
        '&:nth-child(even)': {
          backgroundColor: '#FFFFFF' //white
        },
        '&:nth-child(odd)': {
          backgroundColor: '#D0D5E8' //grey
        },
        border: '1px solid #ddd' // add border to rows
      }
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingLeft: '8px',
        paddingRight: '8px',
        width: '20%',
        borderRight: '1px solid #ddd', // add right border to cells
        justifyContent: 'center'
      }
    }
  };

  // =============================================================================
  const handleSort = (
    column: TableColumn<FetchedEvent>,
    _direction: 'asc' | 'desc'
  ) => {
    setSortState({
      id: column.id as string,
      direction: _direction
    });
  };

  // =============================================================================
  const sortIcon = <ArrowDownward />;

  const filteredData = useMemo(() => {
    const fBuoyId = filterLocation.split('-')[0];
    const fEtId = filterEvent.split('-')[0];
    return fetchedData.filter((d) => {
      const matchLoc =
        fBuoyId === 'ALL' || (d.buoyId as unknown as string) == fBuoyId;
      const matchEvt =
        fEtId === 'ALL' || (d.eventTypeId as unknown as string) == fEtId;
      return matchLoc && matchEvt;
    });
  }, [fetchedData, filterLocation, filterEvent]);

  const handlePageChange = (page: number, totalRows: number) => {
    if (suppressPageChangeRef.current) {
      suppressPageChangeRef.current = false;
      return;
    }
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (currentRowsPerPage: number) => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    suppressPageChangeRef.current = true;
    setCurrentLimit(currentRowsPerPage);
    setCurrentPage(1);
  };

  const displayData = useMemo(() => {
    if (!sortState.direction || !sortState.id) return filteredData;

    const getValue = (row: FetchedEvent): number | string => {
      switch (sortState.id) {
        case 'id':
          return row.id;
        case 'startTime':
          return row.startTs;
        case 'location':
          return row.locationFF ?? '';
        case 'event':
          return row.eventType ?? '';
        default:
          return '';
      }
    };

    const sorted = [...filteredData].sort((a, b) => {
      const valA = getValue(a);
      const valB = getValue(b);
      let result: number;
      if (valA > valB) {
        result = 1;
      } else if (valA < valB) {
        result = -1;
      } else {
        result = 0;
      }
      return sortState.direction === 'desc' ? -result : result;
    });
    return sorted;
  }, [filteredData, sortState]);

  const handleReset = () => {
    setFilterLocation('ALL-ALL');
    setFilterEvent('ALL-ALL');
  };

  const FilterControls = (
    <div style={{ display: 'flex', gap: 16 }}>
      <FormControl size='small' variant='outlined'>
        <InputLabel id='loc-label'>Location</InputLabel>
        <Select
          labelId='loc-label'
          value={filterLocation}
          label='Location'
          onChange={(e: any) => {
            setCurrentPage(1);
            setFilterLocation(e.target.value);
          }}
          sx={{ minWidth: 120 }}
        >
          {[{ id: 'ALL', location: 'ALL', locationFF: 'All' }, ...buoys].map(
            (obj) => (
              <MenuItem key={obj.id} value={`${obj.id}-${obj.location}`}>
                {obj.locationFF}
              </MenuItem>
            )
          )}
        </Select>
      </FormControl>
      <FormControl size='small' variant='outlined'>
        <InputLabel id='evt-label'>Event</InputLabel>
        <Select
          labelId='evt-label'
          value={filterEvent}
          label='Event'
          onChange={(e) => {
            setCurrentPage(1);
            setFilterEvent(e.target.value);
          }}
          sx={{ minWidth: 120 }}
        >
          {[{ id: 'ALL', eventType: 'ALL' }, ...eventTypes].map((obj) => (
            <MenuItem key={obj.id} value={`${obj.id}-${obj.eventType}`}>
              {obj.eventType}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        type='button'
        variant='primary'
        color='dark'
        onClick={handleReset}
      >
        Reset
      </Button>
    </div>
  );

  return (
    <CustomContainer>
      <div>
        <MyModal open={isAddModalOpen} onClose={handleCloseAddModal}>
          <>
            <AddEventTableModal
              handleCloseAddModal={handleCloseAddModal}
              handleRefreshTable={handleRefreshTable}
            />
          </>
        </MyModal>
        <MyModal open={isEditModalOpen} onClose={handleCloseEditModal}>
          <>
            <EditEventTableModal
              handleCloseEditModal={handleCloseEditModal}
              rowData={editRow!}
              handleRefreshTable={handleRefreshTable}
            />
          </>
        </MyModal>
        <MyModal open={isViewModalOpen} onClose={handleCloseViewModal}>
          <>
            <ViewEventModal
              rowData={viewRow!}
              handleCloseViewEventModal={handleCloseViewModal}
            />
          </>
        </MyModal>

        <div
          style={{
            marginTop: 10,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Button
            type='button'
            variant='primary'
            color='dark'
            onClick={handleAddEvent}
          >
            Add Event
          </Button>
          <div style={{ width: 16 }} />
          <Button
            type='button'
            variant='primary'
            color='dark'
            onClick={handleDownloadCsv}
            disabled={isCsvDownloading}
          >
            {isCsvDownloading ? 'Generating CSV...' : 'Download CSV'}
          </Button>
        </div>
        <DataTable
          title='Event Log'
          columns={columns}
          data={displayData}
          sortServer
          onSort={handleSort as any}
          subHeader
          subHeaderComponent={FilterControls}
          progressPending={loading}
          pagination
          paginationServer
          paginationPerPage={20}
          paginationTotalRows={fetchedMetaData.total}
          paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
          customStyles={customStyles}
          sortIcon={sortIcon}
          defaultSortAsc={false}
          defaultSortFieldId={sortState.id}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          key={dataTableKey}
        />
      </div>
    </CustomContainer>
  );
}

export default EventsLog;
